// Occasion packs (D-180): a holiday or event look shipped as data, not code. An admin uploads one zip (manifest.json
// plus art), checks the preview and publishes it; between `startsAt` and `endsAt` (Maldives time) it is the current
// occasion: the app wears its colours (unless a player turns occasion looks off in Appearance), Home shows its banner
// and greeting, and players can claim its pack for free. The pack (table design, card back, emoji, picture frame) is
// kept for good once claimed; after `claimUntil` it is sold in the Shop for `priceBoli` (null: not sold). Anyone who
// finishes a game in the window earns the occasion badge. A new occasion needs no code and no deploy.
import { z } from "zod";
export const OccasionId = z.string().regex(/^[a-z][a-z0-9-]{2,31}$/, "Occasion ids are 3 to 32 lowercase letters, numbers or dashes, starting with a letter.");
/** Item ids an occasion grants. `<id>` is the occasion id. */
export const occasionItems = (id) => ({
    table: `table-occ-${id}`,
    back: `back-occ-${id}`,
    emoji: `pack-occ-${id}`,
    frame: `frame-occ-${id}`,
});
/** The design id an occasion's table uses in prefs.tableDesign (`occ-<id>`). */
export const occasionDesignId = (id) => `occ-${id}`;
export const isOccasionDesign = (designId) => typeof designId === "string" && /^occ-[a-z][a-z0-9-]{2,31}$/.test(designId);
export const occasionOfDesign = (designId) => (isOccasionDesign(designId) ? designId.slice(4) : null);
/** Emoji ids are namespaced so they never clash with the built-in catalog: `occ-<occasion>-<emoji>`. */
export const occasionEmojiId = (occasion, emoji) => `occ-${occasion}-${emoji}`;
export const isOccasionEmoji = (reaction) => typeof reaction === "string" && /^occ-[a-z][a-z0-9-]{2,31}-[a-z][a-z0-9]{1,23}$/.test(reaction);
/**
 * How an emoji moves when it pops over the sender's picture. Each emoji is one image, so motion is a preset, not
 * per-part animation: `wave` rocks side to side (a flag), `burst` scales out with a glow (fireworks), `beat` thumps twice
 * (a drum), `float` rises and sways (balloons), `rock` tilts gently (a boat), `pop` is the plain bounce.
 */
export const EMOJI_MOTIONS = ["pop", "wave", "burst", "beat", "float", "rock", "spin"];
const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Colours are #RRGGBB.");
const cssColour = z.string().regex(/^(#[0-9a-fA-F]{6}|rgba?\(\s*[0-9 .,/%]+\))$/, "Colours are #RRGGBB or rgb()/rgba().");
/** File names inside the zip that a manifest may point at. */
const fileName = z.string().regex(/^[a-z0-9][a-z0-9._-]{0,60}\.(jpg|jpeg|png|webp|svg)$/, "Art files are .jpg, .png, .webp or .svg with a simple lowercase name.");
/** Theme tokens an occasion may set over the player's base theme (same rule as custom themes: colours only). */
export const OCCASION_TOKENS = ["--color-canvas", "--color-canvas-top", "--color-surface", "--color-raised", "--color-edge", "--color-text", "--color-text-secondary", "--color-accent", "--color-accent-high", "--color-accent-deep"];
export const OccasionManifest = z.object({
    id: OccasionId,
    name: z.string().min(2).max(40),
    /** One line under the name in the Shop and Appearance ("A gift for 11 November"). */
    tagline: z.string().min(2).max(90),
    /** ISO dates, read as Maldives time (UTC+5): the look and banner run from the start of `starts` to the end of `ends`. */
    starts: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    ends: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Free to claim until the end of this day (defaults to `ends`). */
    claimUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    /** Shop price in Boli after the claim window; null or missing: not sold afterwards. */
    priceBoli: z.number().int().min(0).max(100_000).nullable().optional(),
    look: z.object({
        /** Dark tokens (applied over Ink and other dark themes) and light ones (over Sea glass and other light themes). */
        dark: z.record(z.enum(OCCASION_TOKENS), hex),
        light: z.record(z.enum(OCCASION_TOKENS), hex),
        /** A strip of triangle flags along the top of tab pages, in these colours (2 to 4); empty: none. */
        bunting: z.array(hex).max(4).default([]),
    }),
    home: z.object({
        greeting: z.string().min(2).max(40).optional(),
        banner: z.object({ image: fileName, eyebrow: z.string().max(30).optional(), title: z.string().min(2).max(40), body: z.string().max(90).optional(), cta: z.string().max(20).optional(), href: z.string().regex(/^\/[a-z0-9/?=&_-]*$/).optional() }).optional(),
    }),
    table: z.object({
        name: z.string().min(2).max(30),
        story: z.string().min(2).max(90),
        room: fileName, rim: fileName, felt: fileName,
        tokens: z.object({ line: cssColour, stitch: cssColour, shade: cssColour, glint: cssColour.optional() }),
        swatch: z.object({ felt: hex, rim: hex }),
    }),
    back: z.object({ name: z.string().min(2).max(30), image: fileName }),
    emoji: z.object({
        name: z.string().min(2).max(30),
        items: z.array(z.object({ id: z.string().regex(/^[a-z][a-z0-9]{1,23}$/), name: z.string().min(1).max(20), label: z.string().min(2).max(40), image: fileName, motion: z.enum(EMOJI_MOTIONS) })).min(1).max(12),
    }),
    /** The picture frame: two to four colours around the picture (conic, like the flag frame). */
    frame: z.object({ name: z.string().min(2).max(30), colours: z.array(hex).min(2).max(4) }),
    badge: z.object({ name: z.string().min(2).max(40), blurb: z.string().min(2).max(90) }),
});
/** Maldives is UTC+5 all year. */
export const MV_OFFSET_MS = 5 * 3_600_000;
export const mvDayStart = (iso) => Date.parse(`${iso}T00:00:00Z`) - MV_OFFSET_MS;
export const mvDayEnd = (iso) => mvDayStart(iso) + 86_400_000 - 1;
/** Art URL for a file in an occasion (served by the API, cached for a year, versioned). */
export const occasionArt = (id, file, version) => `/api/occasions/${encodeURIComponent(id)}/art/${encodeURIComponent(file)}?v=${version}`;
//# sourceMappingURL=occasions.js.map